import { ChevronDownIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  StackDivider,
  Text,
  VStack,
} from '@chakra-ui/react'
import { PopulatedThreadMessage, Reaction, reactionIcons, ReactionType } from '@hb/shared'
import { useCallback, useContext, useEffect, useState } from 'react'

import { PopUpMessageContext, useThreadView } from '@hb/react'
import replyIcon from '../../../../icons/reply.svg'
import { SvgIcon } from '../../../../icons/SvgIcon'
import { useMyReactions, useToggleReaction } from '../../../../messaging/hooks/reactions'
import { DeleteConfirmAlert } from '../../../Buttons/DeleteButton'
import { deleteThreadMessage } from '../../utils'

const ThreadMessageMoreActionsMenu = ({ message }: { message: PopulatedThreadMessage }) => {
  const { sentByMe } = message ?? {}
  const { setReplyingTo, setEditingMessage } = useThreadView()
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const { showMessage } = useContext(PopUpMessageContext)
  const handleDelete = useCallback(() => {
    setDeleting(true)
    deleteThreadMessage(message)
      .then(() => setConfirmingDelete(false))
      .catch(err => {
        setDeleting(false)
        showMessage({ text: 'Error deleting message', subText: err.message, type: 'error' })
      })
  }, [message, showMessage])
  return (
    <>
      <Popover trigger="hover" placement="bottom" strategy="fixed" isLazy>
        <PopoverTrigger>
          <IconButton
            variant="unstyled"
            aria-label="more actions"
            minW="0"
            w="30px"
            height="30px"
            display="flex"
            icon={
              <Text fontSize="lg" color="gray.600">
                ⋮
              </Text>
            }
          />
        </PopoverTrigger>
        <Portal>
          <PopoverContent
            bg="gray.50"
            borderRadius="20px"
            overflow="hidden"
            border="none"
            filter="drop-shadow(1px 1px 4px #00000066)"
            w="160px">
            <VStack divider={<Divider />} spacing={0} w="100%">
              <Button
                size="sm"
                w="100%"
                variant="unstyled"
                _hover={{
                  bg: 'gray.100',
                }}
                display="flex"
                gap={1}
                onClick={() => setReplyingTo(message)}
                justifyContent="center"
                aria-label="reply">
                <SvgIcon width="18px" height="18px" opacity={0.7} src={replyIcon} />
                <Text fontSize="sm" color="gray.600">
                  Reply
                </Text>
              </Button>
              {sentByMe ? (
                <Button
                  size="sm"
                  w="100%"
                  variant="unstyled"
                  _hover={{
                    bg: 'gray.100',
                  }}
                  onClick={() => setEditingMessage(message)}
                  display="flex"
                  gap={1}
                  justifyContent="center"
                  aria-label="edit">
                  <EditIcon color="gray.600" />
                  <Text fontSize="sm" color="gray.600">
                    Edit
                  </Text>
                </Button>
              ) : null}
              {sentByMe ? (
                <Button
                  size="sm"
                  w="100%"
                  variant="unstyled"
                  _hover={{
                    bg: 'gray.100',
                  }}
                  display="flex"
                  gap={1}
                  color="red.600"
                  onClick={() => setConfirmingDelete(true)}
                  justifyContent="center"
                  aria-label="delete">
                  <DeleteIcon w={3} color="red.600" />
                  <Text fontSize="sm">Delete</Text>
                </Button>
              ) : null}
            </VStack>
          </PopoverContent>
        </Portal>
      </Popover>
      {confirmingDelete ? (
        <DeleteConfirmAlert
          confirmActive={confirmingDelete}
          itemName="message"
          actionLoading={deleting}
          onConfirm={handleDelete}
          onCancel={() => setConfirmingDelete(false)}
        />
      ) : null}
    </>
  )
}

export const ThreadMessageMenu = ({
  rightJustified,
  message,
}: {
  rightJustified: boolean
  message: PopulatedThreadMessage
}) => {
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setTimeout(() => setMounted(true), 10)
  }, [])
  const { toggleReaction, togglingReaction } = useToggleReaction(
    message.threadType,
    message.threadId,
    message.messageGroupId,
    String(message.createdOn),
  )
  const myReactions = useMyReactions(message.reactions)
  return (
    <HStack
      position="absolute"
      opacity={mounted ? 1 : 0}
      zIndex={1}
      bg="white"
      pointerEvents={mounted ? 'auto' : 'none'}
      boxShadow="0px 0px 3px rgba(0,0,0,0.3)"
      left={rightJustified ? '-80px' : 'unset'}
      right={rightJustified ? 'unset' : '-80px'}
      flexFlow={rightJustified ? 'row-reverse' : 'row'}
      justifyContent={rightJustified ? 'flex-end' : 'flex-start'}
      top="4px"
      spacing={0}
      transition="opacity 0.3s"
      divider={<StackDivider />}
      borderRadius="full">
      <ThreadMessageMoreActionsMenu message={message} />
      <Popover trigger="hover" placement="bottom" strategy="fixed" isLazy>
        <PopoverTrigger>
          <Button px={1} variant="unstyled" height="30px" display="flex">
            <Text fontSize="sm" filter="grayscale(100%)">
              🙂
            </Text>
            <ChevronDownIcon color="gray.600" />
          </Button>
        </PopoverTrigger>
        <Portal>
          <PopoverContent
            bg="gray.100"
            borderRadius="20px"
            overflow="hidden"
            border="none"
            filter="drop-shadow(1px 1px 4px #00000066)"
            w="160px">
            <VStack spacing={0} w="100%">
              <Text py={1} fontSize="sm" fontWeight={600} color="gray.600">
                REACTIONS
              </Text>
              <Flex justifyContent="center" gap={1} pb={1} flexFlow="row wrap" w="100%">
                {Object.keys(reactionIcons).map(type => {
                  const isMyReaction = myReactions.some(reaction => reaction.type === type)
                  return (
                    <IconButton
                      size="sm"
                      borderRadius="full"
                      key={type}
                      boxShadow={isMyReaction ? 'inset 1px 1px 3px #00000033' : 'none'}
                      bg={isMyReaction ? 'white' : 'transparent'}
                      aria-label={type}
                      icon={<Box>{reactionIcons[type as Reaction['type']]}</Box>}
                      onClick={() => toggleReaction(type as ReactionType)}
                      isLoading={togglingReaction === type}
                    />
                  )
                })}
              </Flex>
            </VStack>
          </PopoverContent>
        </Portal>
      </Popover>
    </HStack>
  )
}
